import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AgGridReact } from 'ag-grid-react';
import { PLGrid } from '../../components/common/PLGrid';
import { GridResetButton } from '../../components/common/GridResetButton';
import ExportAgCSVButton from '../../components/common/ExportAgCSVButton';
import { serialNumberColumn } from '../../libs/getSerialNumberColumn';
import { useGridData } from '../../hooks/useGridData';
import { passwordHistoryConfig } from './passwordHistory.config';
import { userConfig } from '../users/user.config';
import { PasswordHistoryRecord } from '../../types/auth.types';
import { useParams, useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronLeft, faKey, faHistory, faUser, faUserShield } from '@fortawesome/free-solid-svg-icons';
import { Card, Col, Row, Spin } from 'antd';
import { ID_OBJ } from '../../types/common.types';

const PasswordHistoryPage: React.FC = () => {
  const gridRef = useRef<AgGridReact>(null);
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [userDetails, setUserDetails] = useState<any | null>(null);
  const [loadingUser, setLoadingUser] = useState(false);

  // Fetch user details from the API
  useEffect(() => {
    if (id) {
      setLoadingUser(true);
      userConfig.api.getOne(id)
        .then((response) => setUserDetails(response.data))
        .finally(() => setLoadingUser(false));
    }
  }, [id]); 
  const perms = { id: id || "" };
  const { rowData, loading } = useGridData<PasswordHistoryRecord,ID_OBJ>(passwordHistoryConfig.api.list, perms);

  const columnDefs = useMemo(() => [
    serialNumberColumn,
    ...passwordHistoryConfig.passwordHistoryTableColumns,
  ], []);

  // Stats for the boxes
  const stats = [
    {
      title: 'Force Reset',
      value: rowData?.filter(record => record.method.includes('temporary password')).length || 0,
      icon: <FontAwesomeIcon icon={faKey} className="text-red-500" />,
    },
    {
      title: 'Total Password Changes',
      value: rowData?.length || 0,
      icon: <FontAwesomeIcon icon={faHistory} className="text-green-500" />,
    },
    {
      title: 'Changed by Self',
      value: rowData?.filter(record => record.userId === record.changedBy).length || 0,
      icon: <FontAwesomeIcon icon={faUser} className="text-blue-500" />,
    },
    {
      title: 'Changed by Admin',
      value: rowData?.filter(record => record.userId !== record.changedBy).length || 0,
      icon: <FontAwesomeIcon icon={faUserShield} className="text-orange-500" />,
    },
  ];

  return (
    <div className="h-full flex flex-col">
      {/* Header with Back Button */}
      <div className="flex justify-between items-center border-b pb-4 mb-6">
        <div className="flex items-center gap-3">
          <FontAwesomeIcon
            icon={faChevronLeft}
            className="text-gray-600 hover:text-black text-xl cursor-pointer"
            title="Go Back To User List"
            onClick={() => navigate(-1)} // Navigate back
          />
          <h2 className="text-xl font-semibold m-0">
            Password Change History of {userDetails?.fullName || 'User'} ({userDetails?.email || 'N/A'})
          </h2>
        </div>
        <div className="flex items-center gap-2">
          <ExportAgCSVButton gridRef={gridRef} fileName={`${passwordHistoryConfig.entityName}List`} />
          <GridResetButton gridRef={gridRef} />
        </div>
      </div>

      {/* Stats Section */}
      <Row gutter={[24, 24]} className="mb-6">
        {stats.map((stat, index) => (
          <Col xs={24} sm={12} md={6} key={index}>
            <Card>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <div style={{ fontSize: 14, color: '#888' }}>{stat.title}</div>
                  <div style={{ fontSize: 24, fontWeight: 600 }}>{stat.value}</div>
                </div>
                <div style={{ fontSize: 28 }}>
                  {stat.icon}
                </div>
              </div>
            </Card>
          </Col>
        ))}
      </Row>

      {/* Table */}
      <div className="flex-1 overflow-x-auto">
        <PLGrid
          ref={gridRef}
          rowData={rowData}
          columnDefs={columnDefs}
          loading={loading}
        />
      </div>
    </div>
  );
};

export default PasswordHistoryPage;